/**
 * TierLock — visually locks a form field with a "🔒 Upgrade to Pro" overlay
 * when the current tier doesn't include the entitlement. Prevents the user
 * from editing the wrapped input; clicking the overlay opens the pricing
 * page. Backend still enforces the same gate — this is UX layer only.
 *
 * Emits funnel events for the Featured-Slot upsell:
 *  · `featured_lock_impression` on mount (deduped per session)
 *  · `featured_lock_click`      on CTA click
 * Only fires when `testId` includes "featured" so we don't over-attribute
 * on unrelated locks (video_hero, custom_domain, etc.).
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, ArrowUpRight } from "lucide-react";
import { trackSpotlightEvent } from "@/lib/spotlightEvents";

export default function TierLock({ enabled, required = "Pro", children, testId, campaignId }) {
    const isFeaturedLock = (testId || "").includes("featured");

    useEffect(() => {
        // Only fire the impression when the lock is actually visible AND
        // we're tracking the Featured-Slot funnel specifically.
        if (!enabled && isFeaturedLock) {
            trackSpotlightEvent(
                "featured_lock_impression",
                { campaign_id: campaignId || null, source: "seo_tab" },
                { dedupe: true },
            );
        }
    }, [enabled, isFeaturedLock, campaignId]);

    if (enabled) return <>{children}</>;

    return (
        <div className="relative" data-testid={testId || "tier-lock"}>
            <div className="pointer-events-none opacity-40 select-none">{children}</div>
            <Link
                to="/pricing/spotlight"
                onClick={() => {
                    if (isFeaturedLock) {
                        trackSpotlightEvent("featured_lock_click", {
                            campaign_id: campaignId || null, source: "seo_tab",
                        });
                    }
                }}
                className="absolute inset-0 grid place-items-center bg-white/70 backdrop-blur-sm border border-dashed border-amber-500 hover:bg-amber-50 transition-colors"
                data-testid={`${testId || "tier-lock"}-cta`}
            >
                <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-amber-800 bg-white px-3 py-1.5 border border-amber-500">
                    <Lock className="w-3 h-3" /> Upgrade to {required} <ArrowUpRight className="w-3 h-3" />
                </span>
            </Link>
        </div>
    );
}
